<template>



  <div class="">

    <p class="title mb-1" style="margin-top: 25px">Yield Distribution</p>
    <p class="has-text-grey" style="margin-bottom: 20px">Manage and distribute yield rewards to token holders</p>

    <div class="columns">
      <div class="column is-12">
        
        <!-- Error message if user is not a manager -->
        <div v-if="errorMessage" class="notification is-danger">
          <p>{{ errorMessage }}</p>
        </div>
        
        <div v-else>
          <!-- Upper section with two columns -->
          <div class="columns">
            <!-- Vault Statistics Section -->
            <div class="column is-6">
              <div class="box has-background-black-ter has-text-white p-4">
                <h2 class="subtitle is-5 has-text-white mb-4">Vault Statistics</h2>
                
                <div class="columns">
                  <div class="column">
                    <p class="is-size-7 has-text-grey-light">Total Assets</p>
                    <p class="is-size-5 has-text-weight-bold">{{ formatNumber(totalAssets) }} <span class="has-text-weight-normal">CMTY</span></p>
                  </div>
                  
                  <div class="column">
                    <p class="is-size-7 has-text-grey-light">Price Per Share</p>
                    <p class="is-size-5 has-text-weight-bold">{{ formatNumber(pricePerShare) }} <span class="has-text-weight-normal">CMTY</span></p>
                  </div>
                  
                  <div class="column">
                    <p class="is-size-7 has-text-grey-light">Total Shares</p>
                    <p class="is-size-5 has-text-weight-bold">{{ formatNumber(totalShares) }}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Add Yield Section -->
            <div class="column is-6">
              <div class="box">
                <h2 class="subtitle is-5">Add Yield to Vault</h2>
                <div class="field">
                  <div class="control">
                    <input class="input" type="number" placeholder="Amount" v-model="yieldAmount">
                  </div>
                </div>
                <div class="field mt-3">
                  <div class="control">
                    <button 
                      class="button is-dark is-fullwidth" 
                      @click="addYield"
                      :disabled="!yieldAmount || isAddingYield"
                    >
                      <i class="ri-add-line mr-2"></i> Add Yield
                    </button>
                  </div>
                </div>
                <!-- Mensajes ahora se muestran como toast -->
              </div>
            </div>
          </div>
          
          <!-- Yield History Section (full width) -->
          <div class="columns mt-4">
            <div class="column is-12">
              <div class="box">
                <h2 class="subtitle is-5">Yield History</h2>
            
                <div v-if="isLoading" class="has-text-centered">
                  <p>Loading yield history...</p>
                </div>
                
                <div v-else-if="yieldEvents.length === 0" class="has-text-centered">
                  <p>No yield events found</p>
                </div>
                
                <div v-else>
                  <table class="table is-fullwidth is-striped">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Transaction</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(event, index) in yieldEvents" :key="index">
                        <td>
                          <div class="tags has-addons">
                            <span class="tag is-dark">{{ formatDatePart(event.timestamp) }}</span>
                            <span class="tag is-info">{{ formatTimePart(event.timestamp) }}</span>
                          </div>
                        </td>
                        <td>
                          <div class="tags has-addons">
                            <span class="tag is-dark">CMTY</span>
                            <span class="tag is-info has-text-weight-bold">{{ formatAmountValue(event.amount) }}</span>
                          </div>
                        </td>
                        <td>
                          <a :href="getExplorerUrl(event.transactionHash)" target="_blank" class="is-size-7">
                            {{ shortenAddress(event.transactionHash) }}
                            <i class="ri-external-link-line ml-1"></i>
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Confirmation Modal -->
    <div class="modal" :class="{'is-active': showConfirmModal}">
      <div class="modal-background" @click="showConfirmModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Confirm Yield Addition</p>
          <button class="delete is-dark" aria-label="close" @click="showConfirmModal = false"></button>
        </header>
        <section class="modal-card-body">
          <p>Are you sure you want to add <strong>{{ formatAmount(yieldAmount) }}</strong> yield to the vault?</p>
          <p class="is-size-7 mt-2">This will transfer tokens from your wallet to the vault contract.</p>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-dark" @click="confirmAddYield">Confirm</button>
          <button class="button is-light" @click="showConfirmModal = false">Close</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import Web3 from 'web3';
import { mapState } from 'vuex';
import * as util from '../utilities/util';

export default {
  name: 'YieldDistributionPage',
  beforeRouteEnter(to, from, next) {
    const store = window.store;
    next('/'); // Redirect to home if wallet is not connected
    return;
  },
  data() {
    return {
      yieldAmount: '',
      yieldEvents: [],
      isLoading: true,
      isAddingYield: false,
      showConfirmModal: false,
      vaultContract: null,
      tokenContract: null,
      orchestratorContract: null,
      isManagerUser: false,
      errorMessage: '',
      totalAssets: '0',
      totalShares: '0',
      pricePerShare: '0',
      web3: new Web3(window.ethereum)
    };
  },
  computed: {
    ...mapState(['account', 'networkId'])
  },
  async mounted() {
    // Initialize contracts
    const util = require('@/utilities/util.js');
    await util.reconnect();
    this.account = await util.getCoinbase();
    
    // Initialize web3
    this.web3 = await util.getWeb3();
    
    this.tokenContract = await util.tokenContractInstance();
    this.vaultContract = await util.vaultContractInstance();
    this.orchestratorContract = await util.OrchestratorContractInstance();
    
    // Check if user has MANAGER_ROLE
    try {
      // Use the constant value of MANAGER_ROLE (keccak256('MANAGER_ROLE'))
      const MANAGER_ROLE = '0x241ecf16d79d0f8dbfb92cbc07fe17840425976cf0667f022fe9877caa831b08';
      
      // Create a custom ABI for AccessControl's hasRole function and YieldAdded event
      const customABI = [
        {
          "inputs": [
            {
              "internalType": "bytes32",
              "name": "role",
              "type": "bytes32"
            },
            {
              "internalType": "address",
              "name": "account",
              "type": "address"
            }
          ],
          "name": "hasRole",
          "outputs": [
            {
              "internalType": "bool",
              "name": "",
              "type": "bool"
            }
          ],
          "stateMutability": "view",
          "type": "function"
        },
        {
          "anonymous": false,
          "inputs": [
            {
              "indexed": false,
              "internalType": "uint256",
              "name": "yieldAmount",
              "type": "uint256"
            }
          ],
          "name": "YieldAdded",
          "type": "event"
        },
        {
          "inputs": [
            {
              "internalType": "uint256",
              "name": "yieldAmount",
              "type": "uint256"
            }
          ],
          "name": "addYield",
          "outputs": [],
          "stateMutability": "nonpayable",
          "type": "function"
        }
      ];
      
      // Get the vault contract address
      const util = require('@/utilities/util.js');
      // const vaultAddress = util.CMTYv_TokenContractAddress;
      
      // Create a contract instance with our custom ABI
      // This will replace the existing vaultContract with one that has the YieldAdded event
      // this.vaultContract = new this.web3.eth.Contract(customABI, vaultAddress);
      // this.isManagerUser = await this.vaultContract.methods.hasRole(MANAGER_ROLE, this.account).call();
      // console.log('Is manager user:', this.isManagerUser);
    } catch (error) {
      console.error('Error checking manager role:', error);
      this.isManagerUser = false;
    }
    
    if (!this.isManagerUser) {
      this.errorMessage = 'You do not have permission to access this page. Only managers can add yield.';
      return;
    }
    
    if (this.isManagerUser) {
      // Get vault stats - only if user is a manager
      this.totalAssets = await this.orchestratorContract.methods.totalAssets().call();
      this.totalAssets = parseFloat(this.web3.utils.fromWei(this.totalAssets, 'ether')).toFixed(2);
      
      this.pricePerShare = await this.orchestratorContract.methods.pricePerShare().call();
      this.pricePerShare = parseFloat(this.web3.utils.fromWei(this.pricePerShare, 'ether')).toFixed(2);
      
      this.totalShares = await this.orchestratorContract.methods.totalShares().call();
      this.totalShares = parseFloat(this.web3.utils.fromWei(this.totalShares, 'ether')).toFixed(2);
      
      // Load yield events only if user is a manager
      await this.loadYieldEvents();
    }
  },
  methods: {
    
    async loadYieldEvents() {
      if (!this.isManagerUser) {
        // Skip loading events if user is not a manager
        return;
      }
      
      this.isLoading = true;
      try {
        // Get YieldAdded events from the vault contract
        const events = await this.vaultContract.getPastEvents('YieldAdded', {
          fromBlock: 0,
          toBlock: 'latest'
        });
        
        console.log('Found', events.length, 'YieldAdded events');
        
        if (events.length > 0) {
          // Process events
          const processedEvents = await Promise.all(events.map(async (event) => {
            const block = await this.web3.eth.getBlock(event.blockNumber);
            return {
              amount: event.returnValues.yieldAmount,
              manager: event.returnValues.manager || this.account, // Fallback if manager not in event
              transactionHash: event.transactionHash,
              blockNumber: event.blockNumber,
              timestamp: block.timestamp * 1000 // Convert to milliseconds
            };
          }));
          
          // Sort by timestamp (newest first)
          this.yieldEvents = processedEvents.sort((a, b) => b.timestamp - a.timestamp);
        } else {
          // If no events found, show empty array
          this.yieldEvents = [];
          console.log('No YieldAdded events found in the specified block range');
        }
      } catch (error) {
        console.error('Error loading yield events:', error);
        this.yieldEvents = [];
      } finally {
        this.isLoading = false;
      }
    },
    
    addYield() {
      if (!this.yieldAmount || isNaN(this.yieldAmount) || this.yieldAmount <= 0) {
        util.toast.fire({ icon: 'warning', title: 'Please enter a valid amount' });
        return;
      }
      
      this.showConfirmModal = true;
    },
    
    async confirmAddYield() {
      this.showConfirmModal = false;
      this.isAddingYield = true;
      util.toast.fire({ icon: 'info', title: 'Processing transaction...' });
      
      try {
        if (!this.account) {
          throw new Error('No account connected');
        }
        
        if (!this.isManagerUser) {
          throw new Error('You do not have permission to add yield');
        }
        
        // Convert amount to wei
        const amountInWei = this.web3.utils.toWei(this.yieldAmount.toString(), 'ether');
        
        // First approve the token transfer
        await this.tokenContract.methods.approve(this.vaultContract._address, amountInWei).send({ from: this.account });
        
        // Add yield
        await this.vaultContract.methods.addYield(amountInWei).send({ from: this.account });
        
        // Success
        util.toast.fire({ icon: 'success', title: `Successfully added ${this.yieldAmount} CMTY yield to the vault` });
        this.yieldAmount = '';
        
        // Update vault stats
        this.totalAssets = await this.orchestratorContract.methods.totalAssets().call();
        this.totalAssets = parseFloat(this.web3.utils.fromWei(this.totalAssets, 'ether')).toFixed(2);
        
        this.pricePerShare = await this.orchestratorContract.methods.pricePerShare().call();
        this.pricePerShare = parseFloat(this.web3.utils.fromWei(this.pricePerShare, 'ether')).toFixed(2);
        
        this.totalShares = await this.orchestratorContract.methods.totalShares().call();
        this.totalShares = parseFloat(this.web3.utils.fromWei(this.totalShares, 'ether')).toFixed(2);
        
        // Reload events
        await this.loadYieldEvents();
      } catch (error) {
        console.error('Error adding yield:', error);
        util.toast.fire({ icon: 'error', title: error.message || 'Error adding yield' });
      } finally {
        this.isAddingYield = false;
      }
    },
    
    formatDatePart(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    },
    
    formatTimePart(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    },
    
    formatAmount(amount) {
      if (!amount) return '0 CMTY';
      
      // If it's already in wei format, convert to ether
      if (amount.toString().length > 10) {
        return this.web3.utils.fromWei(amount.toString(), 'ether') + ' CMTY';
      }
      
      return amount + ' CMTY';
    },
    
    formatAmountValue(amount) {
      if (!amount) return '0';
      
      let value;
      // If it's already in wei format, convert to ether
      if (amount.toString().length > 10) {
        value = this.web3.utils.fromWei(amount.toString(), 'ether');
      } else {
        value = amount;
      }
      
      // Formatear con separadores de miles y 2 decimales
      return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    
    shortenAddress(address) {
      if (!address) return '';
      return address.substring(0, 6) + '...' + address.substring(address.length - 4);
    },
    
    getExplorerUrl(txHash) {
      // Update with the correct explorer URL based on the network
      const explorerBaseUrl = this.networkId === 1 ? 'https://etherscan.io/tx/' : 
                             this.networkId === 369 ? 'https://scan.pulsechain.com/tx/' :
                             'https://testnet.scan.pulsechain.com/tx/';
      return explorerBaseUrl + txHash;
    },
    
    formatNumber(value) {
      if (!value) return '0';
      // Convertir a número si es string
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      // Formatear con separadores de miles y 2 decimales
      return numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
};
</script>

<style scoped>
.table {
  background-color: transparent;
}
</style>
